import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp } from "react-feather";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { apiRequestHandler } from "../../../utils/commonFunctions";
import { getAwbPricingApi } from "../../../redux/sagas/requests/billing/billingRequests";
import {
    IAwbPricingApiResponse,
    IAwbPricingResponse,
} from "../../../types/dashboard/awbPricingInterface";
import { IVasChargeLine } from "../../../types/dashboard/rateCalculatorInterface";

interface IAwbPricingFormValues {
    awb_number: string;
}

const validationSchema = Yup.object().shape({
    awb_number: Yup.string().trim().required("AWB number is required"),
});

const formatAmount = (value: number) =>
    value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatVasLabel = (vasType: string) => {
    const labels: Record<string, string> = {
        DOCKET_CHARGE: "Docket charges",
        OWNER_RISK: "Insurance ROV",
        CARRIER_RISK: "Carrier insurance",
        FUEL_SURCHARGE: "Fuel surcharges",
        COD_CHARGE: "COD charges",
        FM_CHARGE: "FM charges",
        PACKAGE_HANDLING: "Package handling",
        PACKAGE_HANDLING_CHARGE: "Package handling",
    };
    return labels[vasType] ?? vasType.split("_").map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(" ");
};

const ODA_VAS_TYPES = ["ODA-1", "ODA-2", "ODA-3"];

const isOdaCharge = (charge: IVasChargeLine) => ODA_VAS_TYPES.includes(charge.vas_type);

const AwbPricing = () => {
    const [awbPricing, setAwbPricing] = useState<IAwbPricingResponse | null>(null);
    const [freightExpanded, setFreightExpanded] = useState(false);
    const formikResetRef = useRef<(() => void) | null>(null);

    const initialValues: IAwbPricingFormValues = {
        awb_number: "",
    };

    useEffect(() => {
        setFreightExpanded(false);
    }, [awbPricing]);

    const renderPricingResult = () => {
        if (!awbPricing) {
            return (
                <div className="rounded-xl border border-dashed border-gray-200 bg-white p-8 text-center shadow-sm">
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 text-2xl text-gray-300">
                        ₹
                    </div>
                    <h2 className="text-lg font-semibold text-gray-400">AWB pricing appears here</h2>
                    <p className="mx-auto mt-2 max-w-xs text-sm leading-6 text-gray-400">
                        Enter an AWB number and click Get Pricing to see the freight breakdown.
                    </p>
                </div>
            );
        }

        const { volumetric_details, gst_breakup } = awbPricing;
        const gstPercent = gst_breakup.cgst_rate + gst_breakup.sgst_rate;
        const freightSubtotal = awbPricing.total_amount;
        const odaCharges = awbPricing.vas_charges.filter(isOdaCharge);
        const otherVasCharges = awbPricing.vas_charges.filter((charge) => !isOdaCharge(charge));

        return (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-md">
                <div className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white px-5 py-4">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="inline-block rounded-full bg-gray-800 px-3 py-0.5 text-sm font-semibold uppercase tracking-wide text-white">
                                AWB {awbPricing.awb_number}
                            </span>
                            {awbPricing.service_mode && (
                                <span className="inline-block rounded-full bg-gray-200 px-3 py-0.5 text-sm font-semibold uppercase tracking-wide text-gray-800">
                                    {awbPricing.service_mode}
                                </span>
                            )}
                        </div>
                        <div className="text-right">
                            <p className="text-2xl font-bold text-green-600">₹ {formatAmount(awbPricing.grand_total)}</p>
                        </div>
                    </div>

                    <div className="mt-3 grid grid-cols-1 gap-x-1 gap-y-1 sm:grid-cols-2">
                        <div className="space-y-1 text-sm text-gray-600">
                            <p>
                                Forwarding: <strong className="font-bold text-gray-800">{awbPricing.forwarding_number}</strong>
                            </p>
                            <p>
                                Origin: <strong className="font-bold text-gray-800">{awbPricing.origin_zone}</strong>
                            </p>
                            <p>
                                Total Weight: <strong className="font-bold text-gray-800">{volumetric_details.total_actual_weight} kg</strong>
                            </p>
                        </div>
                        <div className="space-y-1 text-sm text-gray-600">
                            <p>
                                Customer: <strong className="font-bold text-gray-800">{awbPricing.customer}</strong>
                            </p>
                            <p>
                                Destination: <strong className="font-bold text-gray-800">{awbPricing.destination_zone}</strong>
                            </p>
                            <p>
                                Charged Weight: <strong className="font-bold text-gray-800">{volumetric_details.chargeable_weight} kg</strong>
                            </p>
                        </div>
                    </div>
                </div>

                <div className="px-5 py-4">
                    <button
                        type="button"
                        onClick={() => setFreightExpanded((prev) => !prev)}
                        className="flex w-full items-center justify-between text-left text-sm font-semibold text-gray-800"
                    >
                        <span>View Freight Breakdown</span>
                        {freightExpanded ? (
                            <ChevronUp size={24} className="text-gray-400" />
                        ) : (
                            <ChevronDown size={24} className="text-gray-400" />
                        )}
                    </button>

                    {freightExpanded && (
                        <div className="mt-3 space-y-3 border-t border-gray-100 pt-3 text-sm">
                            <div className="flex justify-between font-semibold text-gray-800">
                                <span>Freight Charges</span>
                                <span>₹ {formatAmount(freightSubtotal)}</span>
                            </div>

                            <ul className="space-y-2 pl-1">
                                <li className="flex justify-between text-gray-600">
                                    <span>Base freight charges</span>
                                    <span className="font-medium text-gray-900">₹ {formatAmount(awbPricing.basic_freight)}</span>
                                </li>
                                {odaCharges.map((charge) => (
                                    <li
                                        key={`${charge.vas_type}-${charge.rate_basis}`}
                                        className="flex justify-between text-gray-600"
                                    >
                                        <span>ODA Charge</span>
                                        <span className="font-medium text-gray-900">₹ {formatAmount(charge.amount)}</span>
                                    </li>
                                ))}
                                {otherVasCharges.map((charge) => (
                                    <li key={`${charge.vas_type}-${charge.rate_basis}`} className="flex justify-between text-gray-600">
                                        <span>{formatVasLabel(charge.vas_type)}</span>
                                        <span className="font-medium text-gray-900">₹ {formatAmount(charge.amount)}</span>
                                    </li>
                                ))}
                            </ul>

                            <div className="flex justify-between border-t border-gray-100 pt-3 text-gray-800">
                                <span>GST at {gstPercent}%</span>
                                <span className="font-medium text-gray-900">₹ {formatAmount(gst_breakup.total_gst)}</span>
                            </div>
                        </div>
                    )}
                </div>

                <p className="border-t border-gray-50 bg-gray-50 px-5 py-3 text-xs leading-5 text-gray-500">
                    This freight charge is subject to change based on revisions, if any, in weight &amp; dimension
                    measurements, delivery pin code and pricing file.
                </p>
            </div>
        );
    };

    return (
        <main className="edit-page-wrapper bg-gray-50">
            <section className="section-wrapper py-4">
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">AWB Pricing</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Enter an AWB number to view docket-wise freight and charge breakdown.
                    </p>
                </div>

                <div className="grid grid-cols-1 gap-6 xl:grid-cols-[4fr_6fr]">
                    <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
                        <Formik
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            validateOnChange
                            validateOnBlur
                            onSubmit={async (values, { setSubmitting }) => {
                                setAwbPricing(null);

                                const response = await apiRequestHandler(getAwbPricingApi, {
                                    awb_number: values.awb_number.trim(),
                                });

                                if (response?.status && response.data) {
                                    setAwbPricing((response as IAwbPricingApiResponse).data);
                                }

                                setSubmitting(false);
                            }}
                        >
                            {(formikProps) => {
                                formikResetRef.current = () => {
                                    formikProps.resetForm();
                                    setAwbPricing(null);
                                };

                                return (
                                    <Form className="p-5 sm:p-6">
                                        <section className="mb-8">
                                            <h3 className="mb-3 text-sm font-semibold text-gray-800">AWB Details</h3>
                                            <div>
                                                <label htmlFor="awb_number" className="mb-1 block text-sm font-medium text-gray-700">
                                                    AWB Number
                                                </label>
                                                <Field
                                                    autoComplete="off"
                                                    type="text"
                                                    name="awb_number"
                                                    id="awb_number"
                                                    className="tqs-input h-11 w-full"
                                                    placeholder="Enter AWB Number"
                                                />
                                                <ErrorMessage component="div" name="awb_number" className="input-error" />
                                            </div>
                                        </section>

                                        <div className="flex flex-wrap items-center gap-4">
                                            <button
                                                type="submit"
                                                disabled={formikProps.isSubmitting}
                                                className="min-w-[160px] rounded-lg bg-red-600 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-60"
                                            >
                                                {formikProps.isSubmitting ? "Calculating..." : "Get Pricing"}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => formikResetRef.current?.()}
                                                className="text-sm font-medium text-gray-500 underline-offset-2 hover:text-gray-700 hover:underline"
                                            >
                                                Reset
                                            </button>
                                        </div>
                                    </Form>
                                );
                            }}
                        </Formik>
                    </div>

                    <div className="xl:sticky xl:top-4 xl:self-start">{renderPricingResult()}</div>
                </div>
            </section>
        </main>
    );
};

export default AwbPricing;
